import type { AgentUiMessage, GeminiContent } from './types';

const STORAGE_KEY = 'agent.conversation.v1';
const MAX_UI_MESSAGES = 50;

export interface StoredConversation {
  uiMessages: AgentUiMessage[];
  contents: GeminiContent[];
  /** サーバセッションとの対応キー（無いとリロードのたびに別セッションが増える。§16 S2） */
  clientKey?: string;
  /** セッション種別（無いとリロード後に予約セッションが業務扱いに戻る。2026-08-28） */
  sessionMeta?: { kind: 'work' | 'reservation'; anchor?: string; label?: string };
}

export function loadConversation(): StoredConversation | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.uiMessages) || !Array.isArray(parsed.contents)) {
      return null;
    }
    return parsed as StoredConversation;
  } catch {
    return null;
  }
}

export function saveConversation(conversation: StoredConversation): void {
  try {
    const trimmed: StoredConversation = {
      uiMessages: conversation.uiMessages.slice(-MAX_UI_MESSAGES),
      contents: conversation.contents.slice(-MAX_UI_MESSAGES),
      clientKey: conversation.clientKey,
      sessionMeta: conversation.sessionMeta,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
  } catch {
    // ignore quota error
  }
}

export function clearConversation(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

// --- ウィザード途中状態の保存・復元（Track B2, 2026-07-04） ---
// ウィザード途中でブラウザを閉じた/エラーで中断した場合に、
// 次回同じフィールド構成のウィザードが始まったら回答を復元する。

const WIZARD_DRAFT_KEY = 'agent.wizard.v1';
/** ドラフトの有効期間（これより古いものは復元しない） */
export const WIZARD_DRAFT_TTL_MS = 30 * 60 * 1000;

export interface WizardDraft {
  /** フィールド構成の識別子（field key を , 連結） */
  signature: string;
  answers: Record<string, any>;
  savedAt: number;
}

export function saveWizardDraft(draft: WizardDraft): void {
  try {
    localStorage.setItem(WIZARD_DRAFT_KEY, JSON.stringify(draft));
  } catch {
    // ignore
  }
}

export function loadWizardDraft(): WizardDraft | null {
  try {
    const raw = localStorage.getItem(WIZARD_DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.signature !== 'string' || typeof parsed.savedAt !== 'number') return null;
    if (Date.now() - parsed.savedAt > WIZARD_DRAFT_TTL_MS) return null;
    return parsed as WizardDraft;
  } catch {
    return null;
  }
}

export function clearWizardDraft(): void {
  try {
    localStorage.removeItem(WIZARD_DRAFT_KEY);
  } catch {
    // ignore
  }
}
